import { Controller } from "@hotwired/stimulus"
import { upsertPerformanceQueue, getAllQueue } from "../../pwa/offline_queue"
import { showPwaFlash } from "../../pwa/show_pwa_flash"
import { applyOfflineUpdateToForm, clearUnsavedBadges } from "../../pwa/performance_summary"
import { saveTeamsCache, getCachedTeam, updateTeamInCache } from "../../pwa/teams_cache"
import { hasUnsavedChangesOnPage } from "../../pwa/unsaved_changes"

function syncFormAttributes(root) {
  if (!root) return
  root.querySelectorAll("input").forEach(input => {
    const type = input.type
    if (type === "checkbox" || type === "radio") {
      if (input.checked) input.setAttribute("checked", "checked")
      else input.removeAttribute("checked")
    } else if (!["submit", "button", "reset", "image", "file"].includes(type)) {
      input.setAttribute("value", input.value ?? "")
    }
  })
  root.querySelectorAll("select").forEach(select => {
    Array.from(select.options).forEach(opt => {
      if (opt.selected) opt.setAttribute("selected", "selected")
      else opt.removeAttribute("selected")
    })
  })
  root.querySelectorAll("textarea").forEach(textarea => {
    textarea.textContent = textarea.value
  })
}

export default class extends Controller {
  static targets = ["memberSection", "performancesSection"]

  connect() {
    this.#hideAlertsForQueuedForms()
    this.#cacheTeamsFragments()
    this.#syncWorkerVisibility()
    this._onlineHandler = () => this.#restoreUrl()
    this._submitEndHandler = (e) => this.#handleSubmitEnd(e)
    window.addEventListener("online", this._onlineHandler)
    document.addEventListener("turbo:submit-end", this._submitEndHandler)
  }

  disconnect() {
    if (this._onlineHandler) {
      window.removeEventListener("online", this._onlineHandler)
    }
    if (this._submitEndHandler) {
      document.removeEventListener("turbo:submit-end", this._submitEndHandler)
    }
  }

  #handleSubmitEnd(event) {
    const form = event.target
    if (!form) return
    const isPerformanceForm =
      form.hasAttribute?.("data-group-performance-form") ||
      form.id === "work-confirm-form"
    if (!isPerformanceForm) return
    if (!event.detail?.success) return
    this.#scheduleRefreshCache()
  }

  #scheduleRefreshCache() {
    if (this._refreshScheduled) return
    this._refreshScheduled = true
    setTimeout(async () => {
      this._refreshScheduled = false
      await this.#refreshCurrentTeamCache()
    }, 150)
  }

  rememberTeam(event) {
    this._previousTeamId = event.currentTarget.value
  }

  async selectTeam(event) {
    const select = event.currentTarget

    if (hasUnsavedChangesOnPage(this.application) &&
        !window.confirm("未保存の変更があります。班を切り替えますか？")) {
      if (this._previousTeamId !== undefined) select.value = this._previousTeamId
      return
    }

    this._previousTeamId = select.value

    if (navigator.onLine) {
      document.getElementById("team-selector-form").requestSubmit()
      return
    }

    const teamId = select.value
    const teamData = await getCachedTeam(teamId)

    if (!teamData) {
      showPwaFlash("オフラインです。この班のデータはキャッシュされていません。", "error")
      return
    }

    this.#renderTeam(teamData)
    this.#updateUrlForTeam(teamId)
  }

  #renderTeam(teamData) {
    if (this.hasMemberSectionTarget && teamData.member_list_html) {
      this.memberSectionTarget.innerHTML = teamData.member_list_html
    }
    if (this.hasPerformancesSectionTarget && teamData.performances_html) {
      this.performancesSectionTarget.innerHTML = teamData.performances_html
    }
    this.#syncWorkerVisibility()
  }

  #updateUrlForTeam(teamId) {
    if (this._preOfflineUrl === undefined) {
      this._preOfflineUrl = window.location.href
    }
    const url = new URL(window.location.href)
    url.searchParams.set("team_id", teamId)
    url.searchParams.set("mode", "group")
    window.history.replaceState({}, "", url.toString())
  }

  #restoreUrl() {
    if (this._preOfflineUrl !== undefined) {
      window.history.replaceState({}, "", this._preOfflineUrl)
      this._preOfflineUrl = undefined
    }
  }

  async #refreshCurrentTeamCache() {
    const teamId = document.querySelector("#team-selector-form select")?.value
    if (!teamId) return

    // Temporarily destroy Choices.js wrappers so the snapshot is clean
    // (otherwise nested wrappers form when the cached HTML is restored later).
    const choicesControllers = this.#collectChoicesControllers()
    choicesControllers.forEach(ctrl => ctrl.destroyChoices?.())

    try {
      if (this.hasMemberSectionTarget) syncFormAttributes(this.memberSectionTarget)
      if (this.hasPerformancesSectionTarget) syncFormAttributes(this.performancesSectionTarget)

      const updates = {}
      if (this.hasMemberSectionTarget) updates.member_list_html = this.memberSectionTarget.innerHTML
      if (this.hasPerformancesSectionTarget) updates.performances_html = this.performancesSectionTarget.innerHTML

      await updateTeamInCache(teamId, updates)
    } finally {
      choicesControllers.forEach(ctrl => ctrl.reinitChoices?.())
    }
  }

  #collectChoicesControllers() {
    if (!this.hasPerformancesSectionTarget) return []
    const elements = this.performancesSectionTarget.querySelectorAll('[data-controller~="choices"]')
    const controllers = []
    elements.forEach(el => {
      const ctrl = this.application.getControllerForElementAndIdentifier(el, "choices")
      if (ctrl) controllers.push(ctrl)
    })
    return controllers
  }

  async #cacheTeamsFragments() {
    const el = document.getElementById("teams-fragments-data")
    if (!el) return
    try {
      const teams = JSON.parse(el.textContent)
      if (teams && Object.keys(teams).length > 0) {
        await saveTeamsCache(teams)
      }
    } catch (e) {
      console.warn("Failed to cache teams fragments:", e)
    }
  }

  selectAll() {
    this.element.querySelectorAll(".member-checkbox").forEach(cb => {
      cb.checked = true
      this.#updateWorkerBlock(cb.value, true)
    })
  }

  deselectAll() {
    this.element.querySelectorAll(".member-checkbox").forEach(cb => {
      cb.checked = false
      this.#updateWorkerBlock(cb.value, false)
    })
  }

  toggleWorkerVisibility(event) {
    const checkbox = event.currentTarget
    this.#updateWorkerBlock(checkbox.value, checkbox.checked)
  }

  #updateWorkerBlock(workerId, visible) {
    const block = document.getElementById(`group-worker-block-${workerId}`)
    if (!block) return
    block.hidden = !visible
  }

  #syncWorkerVisibility() {
    this.element.querySelectorAll(".member-checkbox").forEach(cb => {
      this.#updateWorkerBlock(cb.value, cb.checked)
    })
  }

  async confirmAll(event) {
    const message = "今日の作業内容を実績として確定します。よろしいですか？"
    if (!window.confirm(message)) return

    const allForms = [...this.element.querySelectorAll("[data-group-performance-form]")]
    const forms = allForms.filter(form => this.#shouldSubmit(form))

    if (forms.length === 0) {
      showPwaFlash("変更はありません。", "success")
      return
    }

    if (!navigator.onLine) {
      for (const form of forms) {
        await this.#queueOffline(form)
        this.#hideUnsavedAlert(form)
        this.#hideNoPerformancesLabel(form)
        applyOfflineUpdateToForm(form)
      }
      await this.#refreshCurrentTeamCache()
      showPwaFlash("オフラインです。オンライン復帰後に自動保存されます。", "success")
      return
    }

    for (const form of forms) {
      await this.#submitAndWait(form)
    }
    
    await new Promise(resolve => setTimeout(resolve, 150))
    await this.#refreshCurrentTeamCache()
  }

  #shouldSubmit(form) {
    // Workers without an attendance rule cannot create performances.
    if (form.dataset.attendanceRuleMissing === "true") return false

    const block = form.closest(".group-worker-block")
    const workerId = block?.dataset.workerId
    if (workerId) {
      const checkbox = document.querySelector(`.member-checkbox[value="${workerId}"]`)
      if (checkbox && !checkbox.checked) return false
    }

    const ctrl = this.application.getControllerForElementAndIdentifier(form, "unsaved-changes")
    if (ctrl?.isDirty) return true
    if (form.querySelector("[data-unsaved-alert]")) return true
    return false
  }

  #submitAndWait(form) {
    return new Promise((resolve) => {
      const handler = () => {
        form.removeEventListener("turbo:submit-end", handler)
        resolve()
      }
      form.addEventListener("turbo:submit-end", handler)
      form.requestSubmit()
    })
  }

  async #queueOffline(form) {
    const formData = new FormData(form)
    const payload = {}

    for (let [key, value] of formData.entries()) {
      if (payload[key]) {
        if (!Array.isArray(payload[key])) {
          payload[key] = [payload[key]]
        }
        payload[key].push(value)
      } else {
        payload[key] = value
      }
    }

    payload["_url"] = form.action
    payload["_method"] = (form.querySelector("[name='_method']")?.value || "patch")

    await upsertPerformanceQueue({
      type: "performance_update",
      payload: payload
    })
  }

  #hideUnsavedAlert(form) {
    form.querySelectorAll("[data-unsaved-alert]").forEach(el => el.remove())
  }

  #hideNoPerformancesLabel(form) {
    form.querySelectorAll("[data-no-performances]").forEach(el => el.remove())
  }

  async #hideAlertsForQueuedForms() {
    const allForms = [...this.element.querySelectorAll("[data-group-performance-form]")]
    if (allForms.length === 0) return

    const queue = await getAllQueue()
    const queuedUrls = new Set(
      queue
        .filter(item => item.type === "performance_update")
        .map(item => item.payload?._url)
        .filter(Boolean)
    )
    if (queuedUrls.size === 0) return

    allForms.forEach(form => {
      if (queuedUrls.has(form.action)) {
        this.#hideUnsavedAlert(form)
        clearUnsavedBadges(form)
      }
    })
  }
}
